import styled, { css, ExecutionContext, DefaultTheme } from 'styled-components';
import { lighten, resolveColor } from 'ui/styles/utils/colors';
import type { IconSizes } from 'ui/styles/themes/icons';
import type { Colors } from 'ui/styles/utils/colors';
import Padding from 'ui/types/padding';

interface IconWrapperProps {
  $size: IconSizes | number;
  $circular: boolean;
  $padded?: Padding;
  $bordered: boolean;
  $color?: Colors;
  $hoverable: boolean;
}

interface IconProps {
  $size: IconSizes | number;
  $loading: boolean;
  disabled: boolean;
}

const getSize = (theme: DefaultTheme, size: IconSizes | number) =>
  typeof size !== 'number' ? theme.icons.sizes[size] : size;

function getIconSize({ theme, $size }: ExecutionContext & IconProps) {
  const size = getSize(theme, $size);

  return css<IconProps>`
    width: ${size}rem;
    height: ${size}rem;
  `;
}

function getDisplay() {
  return css`
    display: inline-flex;
    vertical-align: middle;
    align-items: center;
  `;
}

function getRadius({ theme, $circular, $size }: ExecutionContext & IconWrapperProps) {
  if ($circular) {
    return css<IconWrapperProps>`
      border-radius: 100%;
    `;
  }
}

function getPadding({ theme, $circular, $size, $padded }: ExecutionContext & IconWrapperProps) {
  const size = getSize(theme, $size);

  if ($padded) {
    return css<IconWrapperProps>`
      padding: ${theme.padding[$padded]}rem;
    `;
  }
  if (!$padded && $circular) {
    return css<IconWrapperProps>`
      padding: ${0.5 * size}rem;
    `;
  }
}

function getColor({ theme, $color }: ExecutionContext & IconWrapperProps) {
  return css<IconWrapperProps>`
    color: ${$color ? resolveColor(theme, $color, 'grayMain') : resolveColor(theme, 'grayDark')};
    fill: ${$color ? resolveColor(theme, $color, 'grayMain') : resolveColor(theme, 'grayDark')};
  `;
}

function getBorder({ theme, $color, $bordered }: ExecutionContext & IconWrapperProps) {
  if ($bordered) {
    return css<IconWrapperProps>`
      border: 1px solid ${$color ? resolveColor(theme, $color, 'grayMain') : 'currentColor'};
    `;
  }
}

function getHoverable({ theme, $hoverable, $color }: ExecutionContext & IconWrapperProps) {
  if (!$hoverable) return null;
  if (!$color) return 'cursor: pointer;';

  const color = resolveColor(theme, $color, 'grayMain');
  const lightened = lighten(theme, color);

  return css`
    cursor: pointer;

    &:hover {
      color: ${lightened};
      border-color: ${lightened};
      fill: ${lightened};
    }
  `;
}

function getLoader({ $loading }: ExecutionContext & IconProps) {
  if (!$loading) return null;

  return css`
    animation: spin 2s infinite linear;

    @keyframes spin {
      0% {
        transform: rotate(0deg);
      }

      100% {
        transform: rotate(360deg);
      }
    }
  `;
}

function getDisabled({ theme, disabled }: ExecutionContext & IconProps) {
  if (!disabled) return null;

  return css`
    color: ${resolveColor(theme, 'grayHover')};
    fill: ${resolveColor(theme, 'grayHover')};
  `;
}

export const IconContainer = styled.span<IconWrapperProps>`
  ${getDisplay};
  ${getRadius};
  ${getPadding};
  ${getColor};
  ${getBorder};
  ${getHoverable};
`;

export const Icon = styled.svg<IconProps>`
  ${getIconSize};
  ${getLoader};
  ${getDisabled};
`;
